import { Injectable } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap, finalize, catchError, take } from 'rxjs/operators';
import { Location } from '@angular/common';
import { CognitoServiceService } from './cognito-service.service';
import { QuicksightService } from './user/quicksight.service';
import { TokenPropertyService } from './token-property.service';
import { TilesService } from './user/tiles.service';
import { CloudWatchRumService } from './cloudwatch-rum.service';
import { SESSION_STORAGE_KEYS } from '../../constants';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class TokenResolverService {
  private static readonly REQUESTED_PATH_KEY = 'requestedPath';

  constructor(
    private location: Location,
    private awsCognitoService: CognitoServiceService,
    private quicksightService: QuicksightService,
    private tokenPropertyService: TokenPropertyService,
    private router: Router,
    private tilesService: TilesService,
    private cloudWatchRumService: CloudWatchRumService
  ) {}

  resolve: ResolveFn<any> = () => {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const code: string | null = urlParams.get('code');

    if (!code) {
      return of(null);
    }

    return this.getTokenDetailsFromCognito(code).pipe(
      finalize(() => {
        const storedRequestedPath = (sessionStorage.getItem(TokenResolverService.REQUESTED_PATH_KEY) || '').toLowerCase();
        const requestedAdminRoute = storedRequestedPath.includes('/admin');
        sessionStorage.removeItem(TokenResolverService.REQUESTED_PATH_KEY);

        if (window.location.search.includes('code=') && requestedAdminRoute) {
          const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }else{
          window.location.href = window.location.pathname;
        }
      })
    );
  }

  getTokenDetailsFromCognito(code: string): Observable<any | null> {
    return this.awsCognitoService.getTokenDetailsFromCognito(code).pipe(
      switchMap(async (response: any) => {
        if (!response?.access_token) {
          sessionStorage.setItem('auth_failed', 'true'); // finalize() hard-reload will trigger app.component to redirect
          return response;
        }

        // Set tokens in sessionStorage (tab-isolated)
        sessionStorage.setItem('token', response.access_token);
        sessionStorage.setItem('refresh_token', response.refresh_token);
        sessionStorage.setItem('id_token', response.id_token);
        sessionStorage.setItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY, 'false');
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_HISTORY);
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.CHAT_SESSION);
        
        // Clear embed URL cache for new session
        this.tilesService.clearEmbedUrlCache();
        
        // Notify TokenPropertyService that tokens have been updated
        this.tokenPropertyService.updateTokenState();

        // Record login event in CloudWatch RUM
        this.cloudWatchRumService.recordLoginEvent();

        // Check if QuickSight registration is needed
        const isRegistered = sessionStorage.getItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY) === 'true';
        
        if (!isRegistered) {
          try {
            // Wait for QuickSight registration to complete
            await firstValueFrom(this.quicksightService.checkQuicksightAccess());
            sessionStorage.setItem(SESSION_STORAGE_KEYS.QUICKSIGHT_REGISTERED_KEY, 'true');
          } catch (error) {
            console.error('QuickSight registration failed:', error);
            // Don't block the flow on registration failure
          }
        }

        const hasToken = sessionStorage.getItem('token') || sessionStorage.getItem('id_token');
        const adminAccess = await firstValueFrom(this.tokenPropertyService.propertyHasValue('custom:adminFlag', true));
        const hasInternalAccess = await firstValueFrom(this.tokenPropertyService.hasInternalAccess());

        // Parse ADgroup robustly — handles null, missing, string, or array values
        const rawGroups = this.tokenPropertyService.getPropertyFromToken('custom:ADgroup', 'id_token');
        const groupArr: string[] = Array.isArray(rawGroups)
          ? rawGroups
          : typeof rawGroups === 'string'
          ? rawGroups.match(/\w[\w-]*/g) ?? []
          : [];
        const hasNoInternalGroup = groupArr.length === 0;

        const storedRequestedPath = (sessionStorage.getItem(TokenResolverService.REQUESTED_PATH_KEY) || '').toLowerCase();
        const requestedAdminRoute = storedRequestedPath.includes('/admin');

        const isInternal = sessionStorage.getItem('isInternal') === 'true';
        sessionStorage.removeItem('isInternal');

        const isAdminUser = groupArr.includes('naag-alight-arap-admin-nonprod');

        if (!hasToken) {
          this.router.navigate(['/error']);
        } else if (hasInternalAccess && hasNoInternalGroup) {
          this.router.navigate(['/unauthorized']);
        } else if (isAdminUser && adminAccess && requestedAdminRoute) {
          this.router.navigate(['/admin']);
        } else if (hasInternalAccess && isInternal) {
          this.router.navigate(['/internal']);
        } else if (!hasInternalAccess && !isInternal) {
          this.router.navigate(['/home/recommendations']);
        } else {
          this.router.navigate(['/error']);
        }

        return response;
      }),
      catchError((error) => {
        console.error('Error in token service:', error);
        return of(error);
      })
    );
  }
}
